import { Controller } from "@hotwired/stimulus"

const STEPS = [
  { label: "3 months",  months: 3  },
  { label: "6 months",  months: 6  },
  { label: "12 months", months: 12 },
  { label: "2 years",   months: 24 },
  { label: "3 years",   months: 36 },
  { label: "4 years",   months: 48 },
  { label: "5 years",   months: 60 },
]

export default class extends Controller {
  static targets = [
    "checkbox", "section", "slider", "periodLabel",
    "endDisplay", "endInput",
    "startsOn", "expiresOn", "fromDisplay", "fromInput"
  ]
  static values = { purchasedAt: String }

  connect() {
    this._startDate = this.purchasedAtValue.length > 0
      ? this.#parseDate(this.purchasedAtValue)
      : this.#localToday()
    this._endOverride = null
    if (this.hasFromDisplayTarget && this.purchasedAtValue.length === 0) {
      this.fromDisplayTarget.textContent = this.#formatDate(this._startDate)
    }
    this.hasCheckboxTarget ? this.#sync() : this.#updateDates()
  }

  toggle() {
    this.#sync()
  }

  slide() {
    // Moving the slider clears any manual end date override
    this._endOverride = null
    this.#setSliderDisabled(false)
    this.#updateDates()
  }

  editFrom() {
    this.fromDisplayTarget.classList.add("hidden")
    this.fromInputTarget.value = this.#iso(this._startDate)
    this.fromInputTarget.classList.remove("hidden")
    this.fromInputTarget.focus()
  }

  fromChanged() {
    const parsed = this.#parseDate(this.fromInputTarget.value)
    if (isNaN(parsed)) return
    this._startDate = parsed
    this.fromDisplayTarget.textContent = this.#formatDate(parsed)
    this._endOverride ? this.#applyEndOverride(this._endOverride) : this.#updateDates()
  }

  fromBlurred() {
    this.fromInputTarget.classList.add("hidden")
    this.fromDisplayTarget.classList.remove("hidden")
  }

  editUntil() {
    const current = this._endOverride ?? this.#sliderEndDate()
    this.endDisplayTarget.classList.add("hidden")
    this.endInputTarget.value = this.#iso(current)
    this.endInputTarget.classList.remove("hidden")
    this.endInputTarget.focus()
  }

  untilChanged() {
    const parsed = this.#parseDate(this.endInputTarget.value)
    if (isNaN(parsed)) return
    this.#applyEndOverride(parsed)
  }

  untilBlurred() {
    this.endInputTarget.classList.add("hidden")
    this.endDisplayTarget.classList.remove("hidden")
  }

  // Disable all warranty inputs before the form submits when the checkbox is
  // unchecked so no warranty_attributes params are sent to the server.
  beforeSubmit() {
    if (!this.hasCheckboxTarget || !this.hasSectionTarget) return
    if (!this.checkboxTarget.checked) {
      this.sectionTarget.querySelectorAll("input, textarea, select").forEach(el => {
        el.disabled = true
      })
    }
  }

  // private

  #applyEndOverride(endDate) {
    this._endOverride = endDate
    this.#setSliderDisabled(true)
    this.endDisplayTarget.textContent  = this.#formatDate(endDate)
    this.periodLabelTarget.textContent = this.#humanDuration(this._startDate, endDate)
    this.startsOnTarget.value          = this.#iso(this._startDate)
    this.expiresOnTarget.value         = this.#iso(endDate)
  }

  #sync() {
    const on = this.checkboxTarget.checked
    if (this.hasSectionTarget) this.sectionTarget.classList.toggle("hidden", !on)
    if (on) this.#updateDates()
  }

  #updateDates() {
    const end  = this.#sliderEndDate()
    const step = STEPS[this.sliderTarget.value]

    this.endDisplayTarget.textContent  = this.#formatDate(end)
    this.periodLabelTarget.textContent = step.label
    this.startsOnTarget.value          = this.#iso(this._startDate)
    this.expiresOnTarget.value         = this.#iso(end)
    this.#updateFill()
  }

  #sliderEndDate() {
    return this.#addMonths(this._startDate, STEPS[this.sliderTarget.value].months)
  }

  #setSliderDisabled(disabled) {
    this.sliderTarget.disabled = disabled
    this.sliderTarget.classList.toggle("opacity-40", disabled)
    this.sliderTarget.classList.toggle("cursor-not-allowed", disabled)
    if (disabled) {
      this.sliderTarget.style.background = "var(--color-hearth-600)"
    } else {
      this.#updateFill()
    }
  }

  #updateFill() {
    const pct = (parseInt(this.sliderTarget.value) / parseInt(this.sliderTarget.max)) * 100
    this.sliderTarget.style.background =
      `linear-gradient(to right, var(--color-warm-amber-500) ${pct}%, var(--color-hearth-600) ${pct}%)`
  }

  #humanDuration(start, end) {
    const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24))

    if (totalDays < 30) {
      return `${totalDays} day${totalDays !== 1 ? "s" : ""}`
    }

    let years  = end.getFullYear() - start.getFullYear()
    let months = end.getMonth()    - start.getMonth()
    let days   = end.getDate()     - start.getDate()

    if (days < 0) {
      months--
      days += new Date(end.getFullYear(), end.getMonth(), 0).getDate()
    }
    if (months < 0) {
      years--
      months += 12
    }

    const p = (n, unit) => `${n} ${unit}${n !== 1 ? "s" : ""}`

    if (years * 12 + months < 12) {
      return [p(months, "month"), days > 0 && p(days, "day")].filter(Boolean).join(", ")
    }

    return [p(years, "year"), p(months, "month"), days > 0 && p(days, "day")].filter(Boolean).join(", ")
  }

  #formatDate(date) {
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
  }

  #localToday() {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }

  #parseDate(str) {
    const [y, m, d] = str.split("-").map(Number)
    return new Date(y, m - 1, d)
  }

  #addMonths(date, months) {
    const result = new Date(date)
    result.setMonth(result.getMonth() + months)
    return result
  }

  #iso(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}
